import { useEffect } from "react";
import { Font } from "@react-pdf/renderer";
import { ENGLISH_FONT_FAMILIES } from "components/fonts/constants";
import { getAllFontFamiliesToLoad } from "components/fonts/lib";

// Font variants that are known to be missing
const MISSING_FONT_VARIANTS: Record<string, string[]> = {
  RobotoSlab: ['Italic'], // RobotoSlab-Italic doesn't exist
};

// Flag to track if fonts have been registered (only register once globally)
let fontsRegistered = false;

// Synchronous font registration - happens immediately, not in useEffect
// This ensures fonts are registered BEFORE any PDF component tries to render
const registerAllFonts = () => {
  if (fontsRegistered) return; // Only register once
  
  const allFontFamilies = getAllFontFamiliesToLoad();
  allFontFamilies.forEach((fontFamily) => {
    const missingVariants = MISSING_FONT_VARIANTS[fontFamily] || [];
    const fonts: Array<{src: string; fontWeight?: "bold" | "normal"; fontStyle?: "italic" | "normal"}> = [];
    
    // Always register Regular variant
    fonts.push({
      src: `/fonts/${fontFamily}-Regular.ttf`,
    });
    
    // Register Bold variant if it exists
    if (!missingVariants.includes('Bold')) {
      fonts.push({
        src: `/fonts/${fontFamily}-Bold.ttf`,
        fontWeight: "bold",
      });
    }
    
    // Register Italic variant if it exists
    if (!missingVariants.includes('Italic')) {
      fonts.push({
        src: `/fonts/${fontFamily}-Italic.ttf`,
        fontStyle: "italic",
      });
    } else {
      // For missing italic variants, register regular as fallback for italic style
      fonts.push({
        src: `/fonts/${fontFamily}-Regular.ttf`,
        fontWeight: "normal",
        fontStyle: "italic",
      });
    }
    
    try {
      Font.register({
        family: fontFamily,
        fonts,
      });
    } catch (error) {
      console.warn(`Failed to register font family ${fontFamily}:`, error);
    }
  });
  
  fontsRegistered = true;
};

export const useRegisterReactPDFFont = () => {
  // Register fonts synchronously on module load, not in useEffect
  // This ensures fonts are available BEFORE useMemo creates PDF components
  registerAllFonts();
};

/**
 * Register all fonts to React PDF so it can render fonts correctly in PDF
 */
// export const useRegisterReactPDFFont = () => {
//   useEffect(() => {
//     const allFontFamilies = getAllFontFamiliesToLoad();
//     allFontFamilies.forEach((fontFamily) => {
//       Font.register({
//         family: fontFamily,
//         fonts: [
//           {
//             src: `/fonts/${fontFamily}-Regular.woff`,
//           },
//           {
//             src: `/fonts/${fontFamily}-Bold.woff`,
//             fontWeight: "bold",
//           },
//           {
//             src: `/fonts/${fontFamily}-Italic.woff`,
//             fontStyle: "italic",
//           },
//         ],
//       });
//     });
//   }, []);
// };

// Track which specific fonts have been registered
const registeredSpecificFonts = new Set<string>();

// Synchronous specific font registration
const registerSpecificFont = (fontFamily: string) => {
  if (registeredSpecificFonts.has(fontFamily)) return; // Already registered
  
  const missingVariants = MISSING_FONT_VARIANTS[fontFamily] || [];
  const fonts: Array<{src: string; fontWeight?: "bold" | "normal"; fontStyle?: "italic" | "normal"}> = [];
  
  // Always register Regular variant
  fonts.push({
    src: `/fonts/${fontFamily}-Regular.ttf`,
  });
  
  // Register Bold variant if it exists
  if (!missingVariants.includes('Bold')) {
    fonts.push({
      src: `/fonts/${fontFamily}-Bold.ttf`,
      fontWeight: "bold",
    });
  }
  
  // Register Italic variant if it exists
  if (!missingVariants.includes('Italic')) {
    fonts.push({
      src: `/fonts/${fontFamily}-Italic.ttf`,
      fontStyle: "italic",
    });
  } else {
    // For missing italic variants, register regular as fallback for italic style
    fonts.push({
      src: `/fonts/${fontFamily}-Regular.ttf`,
      fontWeight: "normal",
      fontStyle: "italic",
    });
  }
  
  try {
    Font.register({
      family: fontFamily,
      fonts,
    });
    registeredSpecificFonts.add(fontFamily);
  } catch (error) {
    console.warn(`Failed to register specific font family ${fontFamily}:`, error);
  }
};

/*
 * Register only the specific font family needed for the resume
 * This is more efficient for public preview pages where only one font is used
*/
export const useRegisterSpecificReactPDFFont = (fontFamily: string) => {
  // Register synchronously so font is available immediately
  registerSpecificFont(fontFamily);
};

// export const useRegisterSpecificReactPDFFont = (fontFamily: string) => {
//   useEffect(() => {
//     Font.register({
//       family: fontFamily,
//       fonts: [
//         {
//           src: `/fonts/${fontFamily}-Regular.ttf`,
//         },
//         {
//           src: `/fonts/${fontFamily}-Bold.ttf`,
//           fontWeight: "bold",
//         },
//         {
//           src: `/fonts/${fontFamily}-Italic.ttf`,
//           fontStyle: "italic",
//         },
//       ],
//     });
//   }, [fontFamily]);
// };


export const useRegisterReactPDFHyphenationCallback = (fontFamily: string) => {
  useEffect(() => {
    if (ENGLISH_FONT_FAMILIES.includes(fontFamily as any)) {
      // Disable hyphenation for English Font Family so the word wraps each line
      // https://github.com/diegomura/react-pdf/issues/311#issuecomment-548301604
      Font.registerHyphenationCallback((word) => [word]);
    } else {
      // React PDF doesn't understand how to wrap non-english word on line break
      // A workaround is to add an empty character after each word
      // Reference https://github.com/diegomura/react-pdf/issues/1568
      Font.registerHyphenationCallback((word) =>
        word
          .split("")
          .map((char) => [char, ""])
          .flat()
      );
    }
  }, [fontFamily]);
};

// Ensure fonts are registered immediately when called from imperative code
// synchronously register fonts before calling react-pdf hooks 
export const ensureReactPDFFontsRegistered = () => {
  registerAllFonts();
};

// Ensure a specific font family is registered synchronously
export const ensureSpecificReactPDFFont = (fontFamily: string) => {
  registerSpecificFont(fontFamily);
};
